<template>
  <b-card>
    <slot name="hint" />
    <b-card-title class="mb-0">
      {{ chartTitle }}
    </b-card-title>
    <div class="font-small-3 mb-1 text-muted">
      {{ chartSubtitle }}
    </div>
    <b-card-text>
      <div v-if="loading">
        <loading-container />
      </div>
      <div
        v-else
        class="position-relative"
      >
        <div
          v-if="noData"
          class="no-data-overlay"
        >
          <span class="text-muted">No data available</span>
        </div>
        <app-echart-line
          :key="chartKey"
          :option-data="chartData"
        />
      </div>
      <div
        v-if="(indexUnderlyingVolRatio || indexUnderlyingVolRatio === 0) && showIndexSelector"
        class="d-flex justify-content-between align-items-center"
        style="margin-top: 0.25rem;"
      >
        <span
          class="text-muted"
          style="font-size: 0.9rem;"
        >
          {{ volatilityText }}
        </span>
        <b-button
          v-if="portfolioType === 'u' && showIndexSelector"
          variant="primary"
          class="select-indices-btn"
          @click="$emit('show-index-selector')"
        >
          Select Indices
        </b-button>
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BCardText, BButton,
} from 'bootstrap-vue'
import AppEchartLine from '@core/components/charts/echart/AppEchartLine.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardText,
    BButton,
    AppEchartLine,
    LoadingContainer,
  },
  props: {
    portfolioType: {
      type: [String, Number],
      default: 'u',
    },
    evolutionData: {
      type: Object,
      default: null,
    },
    indexUnderlyingVolRatio: {
      type: Number,
      default: null,
    },
    showIndexSelector: {
      type: Boolean,
      default: true,
    },
    subtitle: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      noData: false,
      chartKey: 0,
      chartData: {
        tooltip: {},
        legend: {},
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      },
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
    chartTitle() {
      return this.portfolioType === 'u' ? 'Historical Performance' : 'Portfolio Evolution'
    },
    chartSubtitle() {
      if (this.subtitle !== null) return this.subtitle
      return this.portfolioType === 'u'
        ? 'Selectable choice of calculated Indices of structured products and benchmark portfolios'
        : 'Portfolio value compared to the underlyings basket and the calculated Structured Product index. Live proportion of products also shown'
    },
    volatilityText() {
      if (!this.indexUnderlyingVolRatio && this.indexUnderlyingVolRatio !== 0) return ''

      // Parse the value - it might be a string or already a percentage
      const numValue = typeof this.indexUnderlyingVolRatio === 'string'
        ? parseFloat(this.indexUnderlyingVolRatio)
        : this.indexUnderlyingVolRatio

      if (Number.isNaN(numValue)) return ''

      // If value is less than 1, assume it's a decimal (e.g., 0.5577 = 55.77%)
      // If value is greater than or equal to 1, assume it's already a percentage
      const percentage = numValue < 1 ? (numValue * 100).toFixed(2) : numValue.toFixed(2)

      if (this.portfolioType === 'u') {
        return `Structured Product Index volatility relative to underlying basket is ${percentage}%`
      }
      return `Portfolio value volatility relative to underlying basket is ${percentage}%`
    },
  },
  watch: {
    evolutionData: {
      handler(data) {
        this.updateChart(data)
      },
      immediate: true,
    },
    skin() {
      // Re-process data when theme changes
      this.updateChart(this.evolutionData)
    },
  },
  methods: {
    updateChart(data) {
      const apexSeries = data?.SHOWDATA?.DATA
      if (!this.hasSeriesData(apexSeries)) {
        this.setNoData()
        return
      }

      try {
        this.processEvolutionData(data)
      } catch (err) {
        console.error('Error processing portfolio evolution chart:', err)
        this.setNoData()
      }
    },
    hasSeriesData(apexSeries) {
      return Array.isArray(apexSeries) && apexSeries.some(series => (
        Array.isArray(series?.data) && series.data.some(point => point && point.x !== undefined && point.y !== undefined)
      ))
    },
    setNoData() {
      this.noData = true
      this.chartData = {
        tooltip: {},
        legend: { show: false },
        grid: {
          left: 70, right: 50, bottom: 70, top: 60,
        },
        xAxis: { type: 'time' },
        yAxis: { type: 'value' },
        series: [],
      }
      this.chartKey += 1
    },
    processEvolutionData(data) {
      const apexSeries = data.SHOWDATA.DATA.map(series => ({
        ...series,
        data: (series.data || []).filter(point => point && point.x !== undefined && point.y !== undefined),
      })).filter(series => series.data.length > 0)

      if (apexSeries.length === 0) {
        this.setNoData()
        return
      }

      this.noData = false

      // Find min and max values across all series for better y-axis scaling
      let minValue = Infinity
      let maxValue = -Infinity
      apexSeries.forEach(series => {
        series.data.forEach(point => {
          if (point.y < minValue) minValue = point.y
          if (point.y > maxValue) maxValue = point.y
        })
      })

      // Calculate y-axis range with some padding
      const range = maxValue - minValue
      const yAxisMin = Math.floor(minValue - range * 0.1)
      const yAxisMax = Math.ceil(maxValue + range * 0.1)

      // Convert ApexCharts format to ECharts format
      const colors = ['#ff9f43', '#1a73e8', '#ea5455', '#28c76f', '#00cfe8']
      const echartsSeries = apexSeries.map((series, index) => ({
        name: series.name,
        type: 'line',
        data: series.data.map(point => [point.x, point.y]),
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 2,
          color: colors[index % colors.length],
        },
        itemStyle: {
          color: colors[index % colors.length],
        },
      }))

      this.chartData = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
          },
          formatter: params => {
            const date = new Date(params[0].value[0])
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            let result = `${dateStr}<br/>`
            params.forEach(param => {
              const value = param.value[1].toFixed(2)
              result += `${param.marker} ${param.seriesName}: ${value}<br/>`
            })
            return result
          },
        },
        grid: {
          left: 70,
          right: 50,
          bottom: 70,
          top: 60,
          containLabel: false,
        },
        xAxis: {
          type: 'time',
          name: 'Date',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            formatter(value) {
              const date = new Date(value)
              return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            },
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Value',
          nameLocation: 'middle',
          nameGap: 50,
          min: yAxisMin,
          max: yAxisMax,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            formatter: value => value.toFixed(2),
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: echartsSeries,
      }
      // Increment key to force chart re-render
      this.chartKey += 1
    },
  },
}
</script>

<style lang="scss" scoped>
.select-indices-btn {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  line-height: 1.2;
  white-space: nowrap;
}

.no-data-overlay {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  font-size: 1.1rem;
}
</style>
